import { useState } from "react";
import { useAppData } from "../state/AppData";

// Region filter (spec §10). /api/regions is real, backed by a `regions[]`
// config section — however many bbox regions the operator has configured,
// zero by default on a fresh install, which is a legitimate, honestly-shown
// state too ("no regions configured yet"),
// distinct from the control being non-functional (which only happens if
// /api/regions itself hasn't answered — an older backend, or unreachable).
// A node can match more than one selected region now (boxes overlap) —
// see AppData.tsx for the fetch/poll and lib/regions.ts for how membership
// is computed from the confirmed min_lat/max_lat/min_lon/max_lon fields.
export function RegionFilter() {
  const { regions, regionsAvailable, selectedRegions, setSelectedRegions, hideOutsideRegions, setHideOutsideRegions } = useAppData();
  const [open, setOpen] = useState(false);

  function toggle(name: string) {
    const next = new Set(selectedRegions);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelectedRegions(next);
  }

  return (
    <div className="region-filter" style={{ position: "relative" }}>
      <button
        className="region-filter-btn"
        data-functional={regionsAvailable}
        onClick={() => setOpen((o) => !o)}
        title={regionsAvailable ? "Filter the live view to one or more regions" : "Region filter — the region list hasn't loaded yet"}
      >
        Regions{selectedRegions.size > 0 ? ` (${selectedRegions.size})` : ""}
        {!regionsAvailable && <span className="region-filter-pending">·</span>}
      </button>
      {open && (
        <div className="region-filter-menu">
          <div className="panel-header" style={{ padding: "6px 10px" }}>
            <span>Region filter</span>
            <button className="close-btn" onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
          {!regionsAvailable ? (
            <div className="region-filter-stub-note">
              Regions aren't available yet. This control activates automatically once they are (checked every 30s), with
              no page reload needed.
            </div>
          ) : regions.length === 0 ? (
            <div className="region-filter-stub-note">No regions are configured yet. Add one in Settings → Regions.</div>
          ) : (
            <>
              <ul style={{ listStyle: "none", margin: 0, padding: "4px 0" }}>
                {regions
                  .filter((r): r is { name: string } => typeof r.name === "string")
                  .map((r) => (
                    <li key={r.name}>
                      <label className="region-filter-row">
                        <input type="checkbox" checked={selectedRegions.has(r.name)} onChange={() => toggle(r.name)} />
                        {r.name}
                      </label>
                    </li>
                  ))}
              </ul>
              {selectedRegions.size > 0 && (
                <label className="region-filter-row" style={{ borderTop: "1px solid var(--border)" }}>
                  <input type="checkbox" checked={hideOutsideRegions} onChange={(e) => setHideOutsideRegions(e.target.checked)} />
                  Hide others (instead of dimming)
                </label>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
